import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { authApi, saasApi } from '@/services/api';
import {
  Menu,
  Landmark,
  UploadCloud,
  MessageCircle,
  Ticket,
  Send,
  Loader2,
  CheckCircle2,
  Clock,
  History,
  AlertCircle,
  BadgeCheck,
  CreditCard,
  Zap,
  Copy,
  Check,
} from 'lucide-react';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import PackagesModal from '@/components/subscription/PackagesModal';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { PRIMARY_PAYMENT_ACCOUNT } from '@/constants/paymentAccounts';
import { isCompanyAdmin } from '@/types';
import { formatMoney, getCurrencySymbol } from '@/utils/currency';

type SubmitState = 'idle' | 'loading' | 'success' | 'error';

const WHATSAPP_DISPLAY = '+92 3 111 777 183';
const WHATSAPP_DIGITS = '923111777183';
const MAX_PROOF_FILE_BYTES = 5 * 1024 * 1024;

const inputClasses =
  'w-full h-10 border border-gray-300 rounded-lg focus:ring-[3px] focus:ring-primary-500/20 focus:border-primary-600 transition-all px-3 text-sm';
const labelClasses = 'text-xs font-semibold text-gray-500';

function SubmitButton({
  state,
  idleLabel,
  className,
}: {
  state: SubmitState;
  idleLabel: string;
  className: string;
}) {
  return (
    <button type="submit" disabled={state === 'loading' || state === 'success'} className={className}>
      {state === 'loading' && (
        <>
          <Loader2 className="w-4 h-4 animate-spin shrink-0" /> Processing...
        </>
      )}
      {state === 'success' && (
        <>
          <CheckCircle2 className="w-4 h-4 shrink-0" /> Success
        </>
      )}
      {state === 'error' && (
        <>
          <AlertCircle className="w-4 h-4 shrink-0" /> Try Again
        </>
      )}
      {state === 'idle' && idleLabel}
    </button>
  );
}

export default function SupportPage() {
  const { t } = useTranslation();
  const { toggleSidebar } = useUIStore();
  const { role, company } = useAuthStore();
  const admin = isCompanyAdmin(role);

  const bank = PRIMARY_PAYMENT_ACCOUNT;
  const companyCode = company?.code || '—';

  const { data: profile, refetch: refetchProfile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => { const res = await authApi.getCompanyProfile(); return res.data.message; },
  });

  const { data: trialStatus } = useQuery({
    queryKey: ['trialStatus'],
    queryFn: async () => { const res = await saasApi.getTrialStatus(); return res.data.message; },
  });

  const { data: pendingInvoice, refetch: refetchPendingInvoice } = useQuery({
    queryKey: ['pendingSubscriptionInvoice'],
    queryFn: async () => { const res = await saasApi.getPendingSubscriptionInvoice(); return res.data.message; },
    enabled: isCompanyAdmin(role),
  });

  const { data: packages = [] } = useQuery({
    queryKey: ['packages'],
    queryFn: async () => {
      const res = await saasApi.getPackages();
      return res.data.message || [];
    },
    enabled: isCompanyAdmin(role),
  });

  const { data: subscriptionHistory, refetch: refetchSubscriptionHistory } = useQuery({
    queryKey: ['subscriptionHistory'],
    queryFn: async () => { const res = await saasApi.getSubscriptionHistory(); return res.data.message; },
    enabled: isCompanyAdmin(role),
  });

  const status = trialStatus?.status || profile?.status || 'Trial Active';
  const daysRemaining = trialStatus?.days_remaining || profile?.subscription_days_remaining || 0;
  const isTrial = status.includes('Trial');

  const packageName = isTrial ? 'Free Trial' : (profile?.package?.package_name || profile?.package?.name || 'Active Plan');

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    return dateStr;
  };

  const startDateLabel = isTrial ? formatDate(trialStatus?.start_date) : formatDate(profile?.subscription_details?.start_date);
  const endDateLabel = isTrial ? formatDate(trialStatus?.expiry_date) : formatDate(profile?.subscription_details?.end_date);
  const amountLabel = isTrial ? formatMoney(0) : formatMoney(profile?.subscription_details?.amount_paid || 0);
  const billingCycle = isTrial ? 'Trial Period' : (profile?.subscription_details?.billing_cycle || 'Monthly');
  const daysRemainingLabel = `${daysRemaining} Days Left`;

  const totalDays = isTrial ? 14 : (billingCycle === 'Yearly' ? 365 : 30);
  const usagePercent = Math.min(100, Math.max(0, (daysRemaining / totalDays) * 100));
  const isActive = status === 'Active' || status === 'Trial Active';

  const [showPackages, setShowPackages] = useState(false);

  // Copy to clipboard state
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(label);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Payment upload states
  const [payState, setPayState] = useState<SubmitState>('idle');
  const [payError, setPayError] = useState<string | null>(null);
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [paymentDate, setPaymentDate] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [notes, setNotes] = useState('');
  const [manualAmount, setManualAmount] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('');
  const payFormRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const packageCode =
      pendingInvoice?.package_code
      || profile?.package?.package_code
      || profile?.package?.code
      || '';
    if (packageCode) setSelectedPackage(packageCode);
  }, [pendingInvoice?.package_code, profile?.package?.package_code, profile?.package?.code]);

  useEffect(() => {
    setManualAmount(String(pendingInvoice?.total_amount || 0));
  }, [pendingInvoice?.name, pendingInvoice?.total_amount]);

  // Ticket states
  const [ticketState, setTicketState] = useState<SubmitState>('idle');
  const [ticketError, setTicketError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const ticketFormRef = useRef<HTMLFormElement>(null);

  const handlePaymentSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (payState === 'loading') return;
    if (!selectedPackage) {
      setPayError('Please select a package.');
      setPayState('error');
      return;
    }
    if ((Number(manualAmount) || 0) <= 0) {
      setPayError('Please enter a paid amount greater than zero.');
      setPayState('error');
      return;
    }
    if (!screenshot) {
      setPayError('Please attach your payment screenshot.');
      setPayState('error');
      return;
    }
    if (screenshot.size > MAX_PROOF_FILE_BYTES) {
      setPayError('Payment screenshot must be 5MB or smaller.');
      setPayState('error');
      return;
    }
    // if (!pendingInvoice) {
    //   setPayError('No pending invoice found.');
    //   setPayState('error');
    //   return;
    // }

    setPayError(null);
    setPayState('loading');

    try {
      const formData = new FormData();
      formData.append('file', screenshot);
      const uploadRes = await saasApi.uploadFile(formData);
      const fileUrl = uploadRes.data.message.file_url;

      await saasApi.submitSubscriptionPaymentProof({
        invoice_name: pendingInvoice?.name,
        amount: manualAmount,
        package: selectedPackage,
        payment_proof: fileUrl,
        payment_date: paymentDate,
        transaction_reference: transactionId,
        remarks: notes,
      });

      setPayState('success');
      window.setTimeout(() => {
        setPayState('idle');
        setScreenshot(null);
        setTransactionId('');
        setNotes('');
        setPaymentDate('');
        payFormRef.current?.reset();
        refetchPendingInvoice();
        refetchProfile();
        refetchSubscriptionHistory();
      }, 1500);
    } catch (err: any) {
      setPayError(err.response?.data?.message || err.message || 'Failed to submit payment proof.');
      setPayState('error');
    }
  };

  const handleTicketSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (ticketState === 'loading') return;
    setTicketError(null);
    setTicketState('loading');
    try {
      await saasApi.createSupportLead({
        subject: 'Support',
        message,
        name: profile?.owner_name || profile?.company_name || 'User',
        email: profile?.email || '',
        mobile: profile?.mobile || '',
      });
      setTicketState('success');
      window.setTimeout(() => {
        setTicketState('idle');
        setMessage('');
        ticketFormRef.current?.reset();
      }, 1500);
    } catch (err: any) {
      setTicketError(err.response?.data?.message || err.message || 'Failed to submit support ticket.');
      setTicketState('error');
    }
  };

  const renderCopyBtn = (text: string, label: string) => {
    const isCopied = copiedField === label;
    return (
      <button
        type="button"
        onClick={() => handleCopy(text, label)}
        className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-primary-600 transition-all shrink-0 flex items-center justify-center min-w-[24px] h-[24px]"
        title={`Copy ${label}`}
      >
        {isCopied ? (
          <Check className="w-3.5 h-3.5 text-emerald-600 animate-in fade-in" />
        ) : (
          <Copy className="w-3.5 h-3.5" />
        )}
      </button>
    );
  };

  const renderDetailRowCompact = (label: string, value: string, isCopyable: boolean = false) => {
    return (
      <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0 text-sm gap-2">
        <span className="text-gray-500 font-semibold text-xs">{label}</span>
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="font-bold text-gray-900 truncate font-mono text-xs">{value}</span>
          {isCopyable && renderCopyBtn(value, label)}
        </div>
      </div>
    );
  };

  if (!admin) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h2 className="text-xl font-semibold text-gray-900">{t('support.accessDenied')}</h2>
            <p className="text-gray-500 mt-2">Only the Company Admin can access Payment & Support.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto flex flex-col">
        <TrialBanner />

        <div className="p-6 md:p-8 space-y-6 max-w-[1440px] w-full mx-auto">
          {/* Header */}
          <header className="flex items-start gap-3">
            <button
              type="button"
              onClick={toggleSidebar}
              className="lg:hidden p-2 -ml-2 mt-1 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors shrink-0"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl md:text-[30px] font-black tracking-tight text-gray-900 leading-none">
                {t('support.title')}
              </h1>
              <p className="text-xs text-gray-500 mt-1">
                {t('support.subtitle')}
              </p>
            </div>
          </header>

          {/* Grid Layout (Two Columns) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

            {/* Left Column (Subscription Status & Info) (lg:col-span-5) */}
            <div className="lg:col-span-5 space-y-6">

              {/* Subscription Overview Card */}
              <div className="card p-5 bg-white border border-gray-200 shadow-sm relative overflow-hidden">
                <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                  <span className="text-xs font-black uppercase text-gray-400 tracking-wider">Subscription Overview</span>
                  {isActive ? (
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide flex items-center gap-1 bg-green-50 border border-green-200 text-green-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                      {status}
                    </span>
                  ) : (
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide flex items-center gap-1 bg-red-50 border border-red-200 text-red-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                      {status}
                    </span>
                  )}
                </div>

                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-xl font-black text-gray-900">
                    Plan: <span className="text-primary-600">{packageName}</span>
                  </h3>
                  <button 
                    onClick={() => setShowPackages(true)} 
                    className="text-xs font-bold text-primary-600 bg-primary-50 px-3 py-1.5 rounded-lg hover:bg-primary-100 transition-colors"
                  >
                    View Packages
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4 border-t border-b border-gray-100 py-3 mb-4">
                  <div>
                    <span className="text-[10px] text-gray-400 font-bold uppercase">Start Date</span>
                    <p className="text-xs font-bold text-gray-800 mt-0.5">{startDateLabel}</p>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-400 font-bold uppercase">Expiry Date</span>
                    <p className="text-xs font-bold text-gray-800 mt-0.5">{endDateLabel}</p>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-400 font-bold uppercase">Rate</span>
                    <p className="text-xs font-bold text-primary-600 mt-0.5">{amountLabel}</p>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-400 font-bold uppercase">Billing Cycle</span>
                    <p className="text-xs font-bold text-gray-800 mt-0.5">{billingCycle}</p>
                  </div>
                </div>

                <div className="bg-primary-50/50 rounded-xl p-3.5 border border-primary-100">
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-xs font-bold text-gray-700">Days Remaining</span>
                    <span className="text-xs font-extrabold text-primary-700">{daysRemainingLabel}</span>
                  </div>
                  <div className="h-2.5 w-full bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary-600 transition-all duration-500"
                      style={{ width: `${usagePercent}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-gray-500 text-center mt-2 font-medium">
                    {status === 'Active' ? 'Billing subscription active' :
                      status === 'Trial Active' ? 'Currently on free evaluation trial' :
                        'Subscription expired. Please pay to activate.'}
                  </p>
                </div>
              </div>

              {/* Bank Payment Details Card */}
              <div className="card p-5 bg-white border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Landmark className="w-5 h-5 text-primary-600" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Bank Details</h3>
                </div>

                <div className="bg-gray-50 rounded-xl p-3 border border-gray-200 divide-y divide-gray-100">
                  {renderDetailRowCompact("Bank Name", bank.bank_name)}
                  {renderDetailRowCompact("Account Title", bank.account_title)}
                  {renderDetailRowCompact("Account Number", bank.account_number, true)}
                  {renderDetailRowCompact("IBAN", bank.iban, true)}
                  {renderDetailRowCompact("Ref Code", companyCode, true)}
                </div>

                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 p-2.5 rounded-lg mt-3 flex items-start gap-1.5 font-medium leading-normal">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                  <span>
                    Include company reference code <strong className="font-bold underline">{companyCode}</strong> in bank transfer notes to auto-approve.
                  </span>
                </p>
              </div>

              {/* Billing Quick Notes */}
              <div className="card p-5 bg-white border border-gray-200 shadow-sm space-y-4">
                <h4 className="text-xs font-black uppercase text-gray-400 tracking-wider">Helpful Billing Guidelines</h4>

                <div className="space-y-3">
                  <div className="flex gap-2.5">
                    <BadgeCheck className="w-4 h-4 text-primary-600 shrink-0 mt-0.5" />
                    <div>
                      <h5 className="text-xs font-bold text-gray-900">Verification Speed</h5>
                      <p className="text-[11px] text-gray-500 leading-normal">Payments are validated manually within 2–4 business hours during working days.</p>
                    </div>
                  </div>
                  <div className="flex gap-2.5">
                    <CreditCard className="w-4 h-4 text-primary-600 shrink-0 mt-0.5" />
                    <div>
                      <h5 className="text-xs font-bold text-gray-900">Auto Processing</h5>
                      <p className="text-[11px] text-gray-500 leading-normal">Using the correct reference code will expedite automatic validation.</p>
                    </div>
                  </div>
                  <div className="flex gap-2.5">
                    <Zap className="w-4 h-4 text-primary-600 shrink-0 mt-0.5" />
                    <div>
                      <h5 className="text-xs font-bold text-gray-900">Urgent Activation</h5>
                      <p className="text-[11px] text-gray-500 leading-normal">Need activation urgently? Send your transfer slip directly to our WhatsApp support.</p>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Right Column (Upload Form, Support hub, History) (lg:col-span-7) */}
            <div className="lg:col-span-7 space-y-6">

              {/* Payment review status alerts */}
              {pendingInvoice && pendingInvoice.paid === 1 && (
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl shadow-sm flex items-start gap-3 animate-in fade-in">
                  <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-bold text-amber-800">Transfer Slip Under Review</h3>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Your transfer slip for {formatMoney((pendingInvoice.total_amount || 0))} has been submitted. Our accountant will verify it shortly.
                    </p>
                  </div>
                </div>
              )}

              {/* Submit Payment Proof Form */}
              <div className="card p-5 bg-white border border-gray-200 shadow-sm animate-in fade-in">
                  <div className="flex items-center gap-2 mb-4">
                    <UploadCloud className="w-5 h-5 text-primary-600" />
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Submit Transfer Screenshot</h3>
                  </div>

                  <form ref={payFormRef} className="space-y-4" onSubmit={handlePaymentSubmit}>
                    <div className="space-y-1">
                      <label className={labelClasses}>Package</label>
                      <select
                        className={inputClasses}
                        required
                        value={selectedPackage}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSelectedPackage(val);
                          const pkg = packages.find((p: any) => p.package_code === val);
                          if (pkg) {
                            const amount = pkg.monthly_price || pkg.annual_price || pkg.quarterly_price || 0;
                            setManualAmount(String(amount));
                          } else {
                            setManualAmount('');
                          }
                        }}
                      >
                        <option value="">Select active package...</option>
                        {packages.map((pkg: any) => (
                          <option key={pkg.package_code} value={pkg.package_code}>
                            {pkg.package_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="group relative flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-xl p-5 bg-gray-50/50 hover:border-primary-500 hover:bg-primary-50/20 transition-all cursor-pointer">
                      <UploadCloud className="w-8 h-8 text-gray-400 group-hover:text-primary-600 mb-1" />
                      <p className="text-xs text-gray-600 group-hover:text-gray-900 text-center font-bold">
                        {screenshot?.name ?? 'Select or Drag screenshot here'}
                      </p>
                      <p className="text-[9px] text-gray-400 mt-0.5">PNG, JPG, PDF (Max 5MB)</p>
                      <input
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        type="file"
                        accept=".png,.jpg,.jpeg,.pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0] ?? null;
                          if (file && file.size > MAX_PROOF_FILE_BYTES) {
                            setScreenshot(null);
                            setPayError('Payment screenshot must be 5MB or smaller.');
                            setPayState('error');
                            e.target.value = '';
                            return;
                          }
                          setPayError(null);
                          if (payState === 'error') setPayState('idle');
                          setScreenshot(file);
                        }}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className={labelClasses}>Payment Date</label>
                        <input
                          className={inputClasses}
                          type="date"
                          required
                          value={paymentDate}
                          onChange={(e) => setPaymentDate(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className={labelClasses}>Paid Amount ({getCurrencySymbol()})</label>
                        <input
                          className={inputClasses}
                          type="number"
                          min="0.01"
                          step="0.01"
                          required
                          value={manualAmount}
                          onChange={(e) => setManualAmount(e.target.value)}
                        />
                        {!pendingInvoice && Number(manualAmount || 0) <= 0 && (
                          <p className="text-[10px] text-amber-600">Enter the amount you paid.</p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className={labelClasses}>Transaction ID / reference</label>
                      <input
                        className={inputClasses}
                        placeholder="e.g. TXN10329004812"
                        type="text"
                        value={transactionId}
                        onChange={(e) => setTransactionId(e.target.value)}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className={labelClasses}>Remarks (Optional)</label>
                      <textarea
                        className="w-full border border-gray-300 rounded-lg focus:ring-[3px] focus:ring-primary-500/20 focus:border-primary-600 transition-all p-3.5 text-xs"
                        placeholder="Type notes or payment gateway details..."
                        rows={2}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                      />
                    </div>

                    {payState === 'error' && payError && <p className="text-xs text-red-600 font-semibold">{payError}</p>}

                    <SubmitButton
                      state={payState}
                      idleLabel="Upload Slip & Request Activation"
                      className={`w-full h-10 rounded-lg text-sm font-bold transition-all shadow flex items-center justify-center gap-2 ${payState === 'success'
                        ? 'bg-green-600 text-white'
                        : payState === 'error'
                          ? 'bg-red-600 text-white'
                          : 'bg-primary-600 text-white hover:bg-primary-700'
                        }`}
                    />
                  </form>
                </div>

              {/* Support Channels Hub (Grid column) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* WhatsApp Support card */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex flex-col justify-between relative overflow-hidden">
                  <div className="absolute -right-4 -top-4 opacity-5">
                    <MessageCircle className="w-32 h-32" />
                  </div>

                  <div>
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="w-10 h-10 bg-[#25d366] rounded-full flex items-center justify-center text-white shadow">
                        <MessageCircle className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-emerald-950 uppercase tracking-wide">WhatsApp Support</h3>
                        <p className="text-[10px] text-emerald-800/80 font-bold">Fast response</p>
                      </div>
                    </div>

                    <p className="text-xs text-emerald-800 leading-normal">
                      Need help with configuration, accounts, or payments? Instant message our team on WhatsApp for prompt service.
                    </p>

                    <div className="bg-white/80 p-2.5 border border-emerald-100 rounded-lg mt-3 inline-block">
                      <span className="text-[9px] text-emerald-700 font-bold uppercase tracking-wider">Chat Hotline</span>
                      <p className="text-sm font-extrabold text-gray-800 mt-0.5">{WHATSAPP_DISPLAY}</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => window.open(`https://wa.me/${WHATSAPP_DIGITS}`, '_blank', 'noopener,noreferrer')}
                    className="mt-4 bg-[#25d366] text-white h-10 rounded-lg text-xs font-black flex items-center justify-center gap-1.5 hover:bg-[#128c7e] transition-colors shadow-sm"
                  >
                    <Send className="w-3.5 h-3.5" /> Chat on WhatsApp
                  </button>
                </div>

                {/* Ticket Generation card */}
                <div className="card p-5 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <Ticket className="w-4 h-4 text-primary-600" />
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Email Support</h3>
                  </div>

                  <form ref={ticketFormRef} className="space-y-3" onSubmit={handleTicketSubmit}>
                    <div className="space-y-1">
                      <label className={labelClasses}>Ticket Message</label>
                      <textarea
                        className="w-full border border-gray-300 rounded-lg focus:ring-[3px] focus:ring-primary-500/20 focus:border-primary-600 transition-all p-2.5 text-xs"
                        placeholder="Explain the technical issue or questions..."
                        rows={3}
                        required
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                      />
                    </div>

                    {ticketState === 'error' && ticketError && (
                      <p className="text-xs text-red-600 font-semibold">{ticketError}</p>
                    )}

                    <div className="flex justify-end pt-1">
                      <SubmitButton
                        state={ticketState}
                        idleLabel="Create Support Case"
                        className={`py-2 px-4 rounded-lg text-xs font-bold transition-colors shadow flex items-center justify-center gap-1.5 ${ticketState === 'success'
                          ? 'bg-green-600 text-white'
                          : ticketState === 'error'
                            ? 'bg-red-600 text-white'
                            : 'bg-gray-900 text-white hover:bg-gray-800'
                          }`}
                      />
                    </div>
                  </form>
                </div>
              </div>

              {/* Invoicing and Subscription History Card */}
              {subscriptionHistory && subscriptionHistory.length > 0 && (
                <div className="card overflow-hidden bg-white border border-gray-200 shadow-sm animate-in fade-in">
                  <div className="p-4 border-b border-gray-100 flex items-center gap-2 bg-gray-50/50">
                    <History className="w-4 h-4 text-primary-600" />
                    <h3 className="text-xs font-black text-gray-600 uppercase tracking-wider">Invoicing History</h3>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs whitespace-nowrap">
                      <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 text-[10px] uppercase font-bold tracking-wider">
                        <tr>
                          <th className="px-4 py-3">Creation Date</th>
                          <th className="px-4 py-3">Package / tier</th>
                          <th className="px-4 py-3">Cycle</th>
                          <th className="px-4 py-3">Amount</th>
                          <th className="px-4 py-3">Verification</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 text-gray-700">
                        {subscriptionHistory.map((item: any) => (
                          <tr key={item.name} className="hover:bg-gray-50/50 transition-colors">
                            <td className="px-4 py-3 font-medium">
                              {new Date(item.creation).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </td>
                            <td className="px-4 py-3 font-bold text-gray-900">{item.package_name || item.package}</td>
                            <td className="px-4 py-3 font-medium text-gray-500">{item.billing_cycle}</td>
                            <td className="px-4 py-3 font-black text-gray-900">{formatMoney((item.paid_amount || 0))}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider border ${item.status === 'Approved' ? 'bg-green-50 border-green-150 text-green-700' :
                                item.status === 'Submitted' || item.status === 'Under Review' ? 'bg-amber-50 border-amber-150 text-amber-700' :
                                  'bg-gray-50 border-gray-150 text-gray-600'
                                }`}>
                                {item.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

            </div>

          </div>
        </div>
      </div>
      <PackagesModal isOpen={showPackages} onClose={() => setShowPackages(false)} />
    </div>
  );
}
